import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface Suggestion {
  brand: string;
  model: string;
  display: string;
}

interface SearchBoxProps {
  initialValue?: string;
  onSearch: (query: string) => void;
}

// 標準化字串：去空格、去特殊符號、轉小寫，方便比對
function normalize(s: string): string {
  return s.toLowerCase().replace(/[\s\-_(),.|/]/g, '');
}

// 模糊比對：支援直接包含 + 字元順序匹配
function fuzzyMatch(target: string, query: string): boolean {
  if (!query.trim()) return false;
  const t = normalize(target);
  const q = normalize(query);
  if (!q) return false;
  // 直接包含（最優先）
  if (t.includes(q)) return true;
  // 字元順序匹配
  let ti = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const found = t.indexOf(q[qi], ti);
    if (found === -1) return false;
    ti = found + 1;
  }
  return true;
}

// 計算匹配分數（越高越相關）
function matchScore(target: string, query: string): number {
  const t = target.toLowerCase();
  const tNorm = normalize(target);
  const q = query.toLowerCase().trim();
  const qNorm = normalize(query);

  // 完全包含原始字串（最高分）
  if (t.includes(q)) return 100;
  // 標準化後包含（型號編號如 A2338、2338 都能匹配）
  if (tNorm.includes(qNorm)) return 95;
  // 開頭匹配
  if (t.startsWith(q) || tNorm.startsWith(qNorm)) return 90;
  // 按空格拆詞，每個詞都包含加分
  const words = q.split(/\s+/);
  const wordScore = words.filter((w) => t.includes(w) || tNorm.includes(normalize(w))).length * 20;
  return wordScore;
}

export default function SearchBox({ initialValue = '', onSearch }: SearchBoxProps) {
  const [inputVal, setInputVal] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [allModels, setAllModels] = useState<Suggestion[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const fetchedRef = useRef(false);

  // 一次性載入所有型號（分品牌並行，確保不漏）
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    const ALL_BRANDS = ['iPhone', 'iPad', 'MacBook', 'Samsung', 'Asus', 'Google'];
    Promise.all(
      ALL_BRANDS.map((b) =>
        supabase
          .from('repair_pricing')
          .select('brand, model')
          .eq('brand', b)
          .limit(10000)
          .then(({ data }) => data ?? [])
      )
    ).then((results) => {
      const seen = new Set<string>();
      const models: Suggestion[] = [];
      results.flat().forEach((d) => {
        const key = d.brand + '||' + d.model;
        if (!seen.has(key)) {
          seen.add(key);
          models.push({ brand: d.brand, model: d.model, display: d.model });
        }
      });
      setAllModels(models);
    });
  }, []);

  // 根據輸入過濾建議
  const updateSuggestions = useCallback((val: string) => {
    if (!val.trim()) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    const q = val.trim();
    const matched = allModels
      .filter((m) => fuzzyMatch(m.model, q) || fuzzyMatch(m.brand, q))
      .map((m) => {
        const score = matchScore(m.model, q) + matchScore(m.brand, q);
        return { ...m, score };
      })
      .filter((m) => m.score > 0 || fuzzyMatch(m.model, q))
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);
    setSuggestions(matched);
    setShowDropdown(matched.length > 0);
    setActiveIdx(-1);
  }, [allModels]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputVal(val);
    updateSuggestions(val);
  };

  const handleSelect = (s: Suggestion) => {
    setInputVal(s.model);
    setSuggestions([]);
    setShowDropdown(false);
    onSearch(s.model);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowDropdown(false);
    onSearch(inputVal);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // 點擊外部關閉
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 高亮匹配字元
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return <span>{text}</span>;
    const q = query.toLowerCase().replace(/\s+/g, '');
    const result: React.ReactNode[] = [];
    let ti = 0;
    const textLower = text.toLowerCase();
    // 先嘗試連續匹配高亮
    const idx = textLower.indexOf(q);
    if (idx !== -1) {
      result.push(<span key="pre">{text.slice(0, idx)}</span>);
      result.push(<span key="match" className="text-orange-500 font-semibold">{text.slice(idx, idx + q.length)}</span>);
      result.push(<span key="post">{text.slice(idx + q.length)}</span>);
      return <>{result}</>;
    }
    // 模糊高亮
    const chars = q.split('');
    let lastIdx = 0;
    chars.forEach((ch, ci) => {
      const found = textLower.indexOf(ch, ti);
      if (found !== -1) {
        if (found > lastIdx) result.push(<span key={`pre-${ci}`}>{text.slice(lastIdx, found)}</span>);
        result.push(<span key={`ch-${ci}`} className="text-orange-500 font-semibold">{text[found]}</span>);
        lastIdx = found + 1;
        ti = found + 1;
      }
    });
    if (lastIdx < text.length) result.push(<span key="tail">{text.slice(lastIdx)}</span>);
    return <>{result}</>;
  };

  const brandIcon: Record<string, string> = {
    iPhone: 'ri-smartphone-line',
    iPad: 'ri-tablet-line',
    MacBook: 'ri-macbook-line',
    Samsung: 'ri-smartphone-line',
    Asus: 'ri-gamepad-line',
    Google: 'ri-google-line',
  };

  return (
    <div className="relative max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="flex items-center bg-white rounded-2xl overflow-visible shadow-sm">
        <div className="w-14 h-14 flex items-center justify-center flex-shrink-0">
          <i className="ri-search-line text-gray-400 text-xl" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={inputVal}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputVal.trim() && suggestions.length > 0 && setShowDropdown(true)}
          placeholder="輸入品牌或型號，如：iPhone Air、16pro、S25..."
          className="flex-1 text-gray-800 placeholder-gray-400 text-sm py-4 pr-4 outline-none bg-transparent"
          autoComplete="off"
        />
        {inputVal && (
          <button
            type="button"
            onClick={() => { setInputVal(''); setSuggestions([]); setShowDropdown(false); }}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-base" />
          </button>
        )}
        <button
          type="submit"
          className="m-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors whitespace-nowrap cursor-pointer"
        >
          搜尋報價
        </button>
      </form>

      {/* 下拉建議 */}
      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl border border-gray-100 overflow-hidden z-50"
          style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
        >
          <div className="px-4 py-2 border-b border-gray-50">
            <span className="text-xs text-gray-400">搜尋建議</span>
          </div>
          {suggestions.map((s, idx) => (
            <button
              key={s.brand + '||' + s.model}
              type="button"
              onMouseDown={() => handleSelect(s)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer ${activeIdx === idx ? 'bg-orange-50' : 'hover:bg-gray-50'}`}
            >
              <div className="w-7 h-7 flex items-center justify-center bg-orange-50 rounded-lg flex-shrink-0">
                <i className={`${brandIcon[s.brand] ?? 'ri-smartphone-line'} text-orange-400 text-sm`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-800 truncate">
                  {highlightMatch(s.model, inputVal)}
                </div>
                <div className="text-xs text-gray-400">{s.brand}</div>
              </div>
              <i className="ri-arrow-right-up-line text-gray-300 text-sm flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
